// EnterButtonListener.js
import { useEffect, useCallback } from "react";

/**
 * Global Enter key to trigger an action.
 *
 * @param {Object}   params
 * @param {Function} params.onEnter          // required: what to run
 * @param {boolean}  params.enabled=true     // master switch
 * @param {boolean}  params.modalOpen=false  // don't fire while a modal is open
 * @param {boolean}  params.requireNoShift=true // ignore Shift+Enter
 */
export function EnterButtonListener({
  onEnter,
  enabled = true,
  modalOpen = false,
  requireNoShift = true,
}) {
  const handler = useCallback(
    (e) => {
      if (!enabled || modalOpen) return;
      if (e.key !== "Enter") return;
      if (e.repeat) return;
      if (requireNoShift && e.shiftKey) return;

      // Don’t hijack typing contexts
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      if (tag === "textarea" || el?.isContentEditable) return;

      // Don’t re-trigger native submit buttons
      if (tag === "button" && el?.type === "submit") return;

      e.preventDefault();
      onEnter?.();
    },
    [enabled, modalOpen, onEnter, requireNoShift]
  );

  useEffect(() => {
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handler]);
}
